import type { extractMediaVariant } from "@opensea/ui-kit"
import type { UploadContext } from "./upload/context"
import { UploadProgress } from "./upload/progress"

const getFileExtension = (file: File) => {
  return file.name.split(".").pop()
}

export const uploadFile = async (
  context: UploadContext,
  file: File,
  onProgress?: (progress: UploadProgress) => void,
  controller?: AbortController,
) => {
  const formData = new FormData()
  const fields = JSON.parse(context.fields)
  let contentType = file.type
  if (!contentType) {
    switch (getFileExtension(file)) {
      case "glb": {
        contentType = "model/gltf-binary"
        break
      }
      case "gltf":
        contentType = "model/gltf+json"
        break
      default:
        break
    }
  }
  formData.append("Content-Type", contentType)
  for (const key of Object.keys(fields)) {
    formData.append(key, fields[key])
  }
  formData.append("file", file) // The file must be the last element

  // Create XMLHttpRequest for progress tracking
  return new Promise<{ token: string }>((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    if (controller) {
      controller.signal.addEventListener("abort", () => {
        xhr.abort()
      })
    }

    if (onProgress) {
      xhr.upload.onprogress = event => {
        if (event.lengthComputable) {
          const progress = new UploadProgress(event.loaded, event.total)
          onProgress(progress)
        }
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ token: context.token })
      } else {
        reject(new Error(xhr.responseText || `HTTP ${xhr.status}`))
      }
    }

    xhr.onerror = () => {
      reject(new Error("Network error occurred"))
    }

    xhr.onabort = () => {
      reject(new Error("Upload aborted"))
    }

    xhr.open(context.method, context.url)
    xhr.send(formData)
  })
}

export const readFileAsDataURL = (file: File) => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = e => {
      const src = e.target?.result
      if (typeof src === "string") {
        resolve(src)
      } else {
        reject(new Error("Failed to read file as DataURL"))
      }
    }

    reader.onerror = () => reject(new Error("Error reading file"))
    reader.readAsDataURL(file)
  })
}

const MEGABYTE_TO_BYTES = 1_048_576

export const getBytesFromMegabytes = (megabytes: number) => {
  return megabytes * MEGABYTE_TO_BYTES
}

/**
 * Formats a file size in bytes to a human-readable string.
 * Uses binary conversion (1024) to match getBytesFromMegabytes.
 *
 * @param bytes - The file size in bytes
 * @returns Formatted string (e.g., "1.5 KB", "2.3 MB", "500 B")
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) {
    return "0 B"
  }

  if (bytes < 1024) {
    return `${bytes} B`
  }

  if (bytes < 1_048_576) {
    const kb = bytes / 1024
    return `${Number.parseFloat(kb.toFixed(1))} KB`
  }

  if (bytes < 1_073_741_824) {
    const mb = bytes / 1_048_576
    return `${Number.parseFloat(mb.toFixed(1))} MB`
  }

  const gb = bytes / 1_073_741_824
  return `${Number.parseFloat(gb.toFixed(1))} GB`
}

type MediaVariant = ReturnType<typeof extractMediaVariant>

export function extractMediaVariantFromDataUri(
  uri: string,
): MediaVariant | undefined {
  if (!uri.startsWith("data:")) {
    return undefined
  }
  const parts = uri.split(";")
  if (parts.length < 2) {
    return undefined
  }
  const mediaType = parts[0].split(":")[1]
  const [type, format] = mediaType.split("/")

  switch (type) {
    case "video":
      return { type: "video", format } as MediaVariant
    case "audio":
      return { type: "audio", format } as MediaVariant
    case "model":
      return { type: "3d", format } as MediaVariant
    case "text":
      if (format === "html") {
        return { type: "html" }
      }
      break
    case "image":
      return { type: "image", format }
    default:
      return undefined
  }
}

/**
 * Converts a URL to a File object by fetching the resource
 * @param url - The URL to fetch and convert to a File
 * @param filename - Optional filename, defaults to extracted from URL
 * @returns Promise that resolves to a File object
 */
export const fetchFileFromUrl = async (
  url: string,
  filename?: string,
): Promise<File> => {
  try {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`)
    }

    const blob = await response.blob()

    // Extract filename from URL if not provided
    let finalFilename = filename
    if (!finalFilename) {
      const urlPath = new URL(url).pathname
      finalFilename = urlPath.split("/").pop() || "image"

      // Add extension if not present
      if (!finalFilename.includes(".") && blob.type) {
        const extension = blob.type.split("/")[1]
        if (extension) {
          finalFilename += `.${extension}`
        }
      }
    }

    return new File([blob], finalFilename, { type: blob.type })
  } catch (error) {
    console.error("Error converting URL to File:", error)
    throw error
  }
}

/**
 * Converts an Accept object to a comma-separated list of file format names
 * @param accept - The Accept object from react-dropzone
 * @returns Formatted string (e.g., "JPG, PNG, SVG, GIF")
 */
export const formatAcceptedFormats = (
  accept: Record<string, string[]>,
): string => {
  const formats = Object.values(accept)
    .flat()
    .map(ext => ext.replace(".", "").toUpperCase())
    .filter((format, index, array) => {
      if (format === "JPEG" && array.includes("JPG")) {
        return false
      }
      return array.indexOf(format) === index
    })
    .sort()

  return formats.join(", ")
}

/**
 * Generates requirements text from accept and maxFileSize using i18n
 * @param accept - The Accept object from react-dropzone
 * @param maxFileSize - Maximum file size in bytes
 * @param t - Translation function
 * @param dimensions - Optional dimensions object
 * @returns Formatted requirements string
 */
export const generateRequirementsText = (
  accept: Record<string, string[]>,
  maxFileSize: number,
  // biome-ignore lint/suspicious/noExplicitAny: TODO FIX
  t: any,
  dimensions?: { width: number; height: number },
): string => {
  const formats = formatAcceptedFormats(accept)
  const fileSize = formatFileSize(maxFileSize)

  if (dimensions) {
    return t("requirements", {
      width: dimensions.width,
      height: dimensions.height,
      formats,
      fileSize,
    })
  }

  return t("requirementsNoSize", {
    formats,
    fileSize,
  })
}
